import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const baseClasses =
  'inline-flex items-center justify-center gap-1 rounded-full font-bold transition-all duration-200 active-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2 focus-visible:ring-offset-theme-surface cursor-pointer';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border border-transparent bg-theme-action-bg text-theme-action-fg hover:bg-theme-action-bg-hover hover:shadow-md dark:hover:shadow-[0_0_14px_rgba(255,255,255,0.15)]',
  secondary:
    'border border-theme-border bg-transparent text-theme-text hover:bg-theme-surface-hover',
  ghost:
    'border border-transparent bg-transparent text-theme-text hover:bg-theme-surface-hover',
  outline:
    'border border-theme-border bg-transparent text-theme-text hover:border-theme-accent hover:bg-theme-surface-hover',
  danger:
    'border border-[color-mix(in_srgb,var(--theme-danger)_25%,transparent)] bg-transparent text-theme-danger hover:bg-[color-mix(in_srgb,var(--theme-danger)_6%,transparent)]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-[8px]',
  md: 'px-3 py-[5px] text-[9px]',
  lg: 'px-4 py-[6px] text-[10px]',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${
        isDisabled ? 'cursor-not-allowed opacity-70' : ''
      } ${className}`}
      disabled={isDisabled}
      {...props}
    >
      {loading && (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80" />
      )}
      {!loading && leftIcon}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
};

export default Button;
